import { differenceInDays } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';
import { mergeMedicationSurveyData } from '../../../../common/mergeMedicationSurveyData';

import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../../components/AlertBox';
import DataTrackerChart from '../../../../components/charts/DataTrackerChart';
import {
  StudyParticipantQueryData,
  StudyParticipantQueryParamsDto,
  useQueryStudyParticipantData
} from '../../../../hooks/study-participant/useQueryStudyParticipantData';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import IconButton from '../../../../components/IconButton';
import { Octicons } from '@expo/vector-icons';
import Card from '../../../../components/Card';
import WebViewModal from '../../../modals/WebViewModal';
import elementIDs from '../../../../../elementIDs';

const sampleData = [
  [
    { x: 1, y: 7 },
    { x: 2, y: 7.9 },
    { x: 3, y: 7.9 },
    { x: 4, y: 8.1 },
    { x: 5, y: 9.5 },
    { x: 6, y: 9.8 },
    { x: 7, y: 10 },
    { x: 8, y: 10.2 },
    { x: 9, y: 10.4 },
    { x: 10, y: 10.6 },
    { x: 11, y: 10.6 },
    { x: 12, y: 10.4 },
    { x: 13, y: 9.4 },
    { x: 14, y: 8.5 },
    { x: 15, y: 8 },
    { x: 16, y: 7.6 },
    { x: 16.5, y: 7.7 }
  ],
  [
    { x: 1, y: 5 },
    { x: 2, y: 5 },
    { x: 3, y: 5 },
    { x: 4, y: 5 },
    { x: 5, y: 5 },
    { x: 6, y: 5 },
    { x: 7, y: 5 },
    { x: 7, y: 6 },
    { x: 7, y: 7 },
    { x: 7, y: 8 },
    { x: 7, y: 9 },
    { x: 7.01, y: 10 },
    { x: 7.05, y: 10 },
    { x: 7.99, y: 10 },
    { x: 7.1, y: 10 },
    { x: 7.2, y: 10 },
    { x: 7.3, y: 10 },
    { x: 7.4, y: 10 },
    { x: 7.5, y: 10 },
    { x: 8, y: 10 },
    { x: 9, y: 10 },
    { x: 10, y: 10 },
    { x: 11, y: 10 },
    { x: 12, y: 10 },
    { x: 13, y: 10 },
    { x: 14, y: 10 },
    { x: 15, y: 10 },
    { x: 16, y: 10 },
    { x: 16.5, y: 10, label: 'Dose (mg)' }
  ]
];

const StyledView = styled(Card)`
  position: relative;
  width: 100%;
`;

const StyledIconButton = styled(IconButton)`
  margin: 16px 24px 0 auto;
`;

export interface DataTrackerChartCardProps {
  title?: string;
  yAxisLeftLabel?: string;
  yAxisRightLabel?: string;
  enableRightAxis?: boolean;
  leftAxisEntityId?: string;
  leftAxisColumnName?: string;
  rightAxisEntityId?: string;
  rightAxisColumnName?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
}

const DataTrackerChartCard: React.FC<
  ScreenComponentProps<DataTrackerChartCardProps>
> = ({
  leftAxisEntityId,
  leftAxisColumnName,
  rightAxisEntityId,
  rightAxisColumnName,
  studyParticipant,
  hasMoreInfo,
  infoTitle,
  infoHtml,
  ...props
}) => {
  const [showModal, setShowModal] = useState<boolean>();
  const { queryResults, loadingQuery, loadingQueryError, queryData } =
    useQueryStudyParticipantData(studyParticipant?.id || 0);
  // build query from component configuration
  useEffect(() => {
    if (studyParticipant?.id) {
      const params: StudyParticipantQueryParamsDto[] = [];
      if (leftAxisEntityId && leftAxisColumnName) {
        params.push({
          entityId: leftAxisEntityId,
          columnNames: [leftAxisColumnName]
        });
      }
      if (props.enableRightAxis && rightAxisEntityId && rightAxisColumnName) {
        params.push({
          entityId: rightAxisEntityId,
          columnNames: [rightAxisColumnName]
        });
      }
      queryData(params);
    }
  }, [
    queryData,
    studyParticipant?.id,
    leftAxisColumnName,
    leftAxisEntityId,
    props.enableRightAxis,
    rightAxisColumnName,
    rightAxisEntityId
  ]);
  // format query data for the chart to display
  const { dataSets, maxX, maxY } = useMemo(() => {
    // make a copy of start date to prevent mutation
    const startDate = studyParticipant?.startDate
      ? new Date(studyParticipant.startDate)
      : undefined;
    // console.log('startDate', startDate);
    if (!studyParticipant?.id)
      return { dataSets: sampleData, maxX: 26, maxY: 13 };
    if (!queryResults || !startDate || !leftAxisColumnName)
      return { dataSets: [], maxX: 10, maxY: 20 };
    const res = mergeMedicationSurveyData(queryResults);
    let maxX = 0;
    let maxY = 0;
    startDate.setHours(0, 0, 0, 0);
    const dataSets = res?.map((data, index) =>
      data
        ?.filter(row => {
          const value =
            index === 0
              ? row[leftAxisColumnName]
              : row[rightAxisColumnName || ''];
          return value && !isNaN(value);
        })
        ?.map(row => {
          row.intendedStartDate.setHours(0, 0, 0, 0);
          const x = differenceInDays(row.intendedStartDate, startDate);
          const y =
            index === 0
              ? row[leftAxisColumnName]
              : row[rightAxisColumnName || ''];
          if (index === 0) {
            maxY = Math.max(maxY, y);
          }
          maxX = Math.max(maxX, x);
          return {
            x,
            y
          };
        })
    );
    if (maxX === 0) maxX = 2;
    if (maxY === 0) maxY = 1;
    return { dataSets, maxX, maxY: maxY * 1.1 };
  }, [
    queryResults,
    leftAxisColumnName,
    rightAxisColumnName,
    studyParticipant?.startDate,
    studyParticipant?.id
  ]);
  if (
    dataSets.length === 2 &&
    props.yAxisRightLabel &&
    dataSets[1][dataSets[1].length - 1]
  ) {
    (dataSets[1][dataSets[1].length - 1] as any).label = props.yAxisRightLabel;
  }
  // console.log('dataSets', dataSets);
  // console.log('sampleData', sampleData);
  return (
    <StyledView testID={elementIDs.pluginDataTrackerID}>
      {hasMoreInfo && (
        <StyledIconButton
          onPress={() => setShowModal(true)}
          testID={elementIDs.pluginDataTrackerMoreInfoID}
        >
          <Octicons name='question' size={24} color='#494753' />
        </StyledIconButton>
      )}
      <DataTrackerChart
        // yAxisLabel='SLEEP DURATION (HRS)'
        data={dataSets}
        xAxisMaxValueLabel={maxX}
        yAxisMaxValueLabel={maxY}
        {...props}
      />
      {loadingQuery && <ActivityIndicatorScreen modal />}
      {loadingQueryError && (
        <AlertBox fullWidth severity='error'>
          Chart error: {loadingQueryError.message}
        </AlertBox>
      )}
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledView>
  );
};

export const DataTrackerChartCardPlugin: ScreenComponentPlugin<DataTrackerChartCardProps> =
  {
    name: 'Data Tracker Chart Card',
    type: 'DataTrackerChartCard',
    categories: ['study-progress', 'my-chloe'],
    component: DataTrackerChartCard
  };
